/**
 * The help bubble, bottom-right of every page.
 *
 * What it is: the shallow end. It answers "where is this" and "why did it do
 * that" from the product help library on the server — written by a person,
 * instant, free, and incapable of inventing a button. What it is not: an AI
 * chat. Vee is one tap away for questions about the law, and a support ticket
 * is one tap away for everything neither can answer.
 *
 * The rule it keeps, which most help widgets break: when it does not know, it
 * says so in one line and offers a person. It never produces a plausible
 * answer to look useful.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { FiHelpCircle, FiX, FiSend, FiExternalLink, FiLifeBuoy } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { helpAPI, ticketAPI } from '../../services/api';
import { ROUTES } from '../../constants/constants';
import { useUI } from '../../context/UIContext';

const TEAL = '#145f5f';
const GOLD = '#8a6a1f';
const BORDER = '#ddd6c6';
const MUTED = '#7a7060';

/** nav keys the help library uses → routes the app has. */
const NAV_ROUTES = {
  sdd: ROUTES.SDD,
  clients: ROUTES.MANAGE_COLLABORATORS,
  companies: ROUTES.COMPANIES,
  dashboard: ROUTES.DASHBOARD,
};

const errText = (e) => e?.response?.data?.message || e?.message || 'Something went wrong.';

export default function HelpWidget() {
  const navigate = useNavigate();
  const { showToast } = useUI();

  const [open, setOpen] = useState(false);
  const [suggested, setSuggested] = useState([]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  // The last thing they asked that got no answer — becomes the ticket subject,
  // so nobody types their problem twice.
  const [unanswered, setUnanswered] = useState('');
  const [raising, setRaising] = useState(false);

  const endRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    if (suggested.length) return;
    helpAPI.topics()
      .then((res) => setSuggested(res?.data?.suggested || []))
      .catch(() => { /* the widget still works by typing */ });
  }, [open, suggested.length]);

  // Optional-called: jsdom has no scrollIntoView, and neither do some embedded
  // browsers. Scrolling is a nicety; throwing here would blank the widget.
  useEffect(() => { endRef.current?.scrollIntoView?.({ behavior: 'smooth' }); }, [messages, busy]);

  // Escape closes it, like every other panel in the app.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const push = (msg) => setMessages((m) => [...m, msg]);

  const askQuestion = useCallback(async (question) => {
    const asked = String(question || '').trim();
    if (!asked || busy) return;

    push({ from: 'user', text: asked });
    setInput('');
    setBusy(true);
    setUnanswered('');

    try {
      const res = await helpAPI.ask(asked);
      const data = res?.data || {};
      if (data.answered) {
        push({ from: 'help', topics: data.topics || [] });
      } else {
        push({ from: 'help', unknown: true, text: data.message });
        setUnanswered(asked);
      }
    } catch (error) {
      push({ from: 'help', unknown: true, text: errText(error) });
      setUnanswered(asked);
    } finally {
      setBusy(false);
    }
  }, [busy]);

  const openSuggested = async (topic) => {
    push({ from: 'user', text: topic.title });
    setBusy(true);
    try {
      const res = await helpAPI.topic(topic.id);
      push({ from: 'help', topics: [res?.data?.topic].filter(Boolean) });
    } catch (error) {
      push({ from: 'help', unknown: true, text: errText(error) });
    } finally {
      setBusy(false);
    }
  };

  /** Hand the unanswered question to a person, with the conversation attached. */
  const raiseTicket = async () => {
    if (!unanswered || raising) return;
    setRaising(true);
    try {
      const transcript = messages
        .filter((m) => m.from === 'user')
        .map((m) => `- ${m.text}`)
        .join('\n');
      const res = await ticketAPI.createTicket({
        category: 'technical',
        subject: unanswered.slice(0, 120),
        description: `Asked in the help widget and not answered:\n\n${unanswered}\n\n`
          + `Everything they asked in this session:\n${transcript}\n\nPage: ${window.location.pathname}`,
      });
      const number = res?.data?.ticket?.ticketNumber;
      push({
        from: 'help',
        text: number
          ? `Raised as ${number}. A person will reply — you will get a notification, and the thread is on Help & Support.`
          : 'Raised. A person will reply on Help & Support.',
      });
      setUnanswered('');
      showToast(number ? `Support ticket ${number} raised.` : 'Support ticket raised.', 'success');
    } catch (error) {
      showToast(errText(error), 'error');
    } finally {
      setRaising(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Help"
        title="Help"
        className="fixed bottom-6 right-6 z-[9990] w-12 h-12 rounded-full shadow-lg flex items-center justify-center text-white transition-transform hover:scale-105 active:scale-95"
        style={{ background: TEAL }}
      >
        <FiHelpCircle size={22} />
      </button>
    );
  }

  return (
    <div
      className="fixed bottom-6 right-6 z-[9990] w-[min(370px,calc(100vw-2rem))] rounded-2xl shadow-2xl flex flex-col overflow-hidden"
      style={{ background: '#fffdf8', border: `1px solid ${BORDER}`, maxHeight: 'min(560px, calc(100vh - 3rem))' }}
      role="dialog"
      aria-label="Help"
    >
      <div className="flex items-center justify-between px-4 py-3" style={{ background: TEAL }}>
        <div className="flex items-center gap-2 text-white">
          <FiLifeBuoy size={16} />
          <span className="text-[13px] font-semibold">Help</span>
        </div>
        <button type="button" onClick={() => setOpen(false)} aria-label="Close help" className="text-white/80 hover:text-white">
          <FiX size={18} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
        {messages.length === 0 && (
          <div className="text-[12px] leading-relaxed" style={{ color: MUTED }}>
            Questions about using ComplyV — where something lives, what a screen means, why it did
            what it did. For questions about the law, ask Vee.
            {suggested.length > 0 && (
              <div className="mt-3 space-y-1.5">
                {suggested.map((topic) => (
                  <button
                    key={topic.id}
                    type="button"
                    onClick={() => openSuggested(topic)}
                    className="block w-full text-left text-[12px] px-3 py-2 rounded-lg transition"
                    style={{ background: '#f4f0e8', color: '#08111f', border: `1px solid ${BORDER}` }}
                  >
                    {topic.title}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} className={m.from === 'user' ? 'flex justify-end' : ''}>
            {m.from === 'user' ? (
              <div className="max-w-[85%] text-[12.5px] px-3 py-2 rounded-2xl rounded-br-sm text-white" style={{ background: TEAL }}>
                {m.text}
              </div>
            ) : (
              <div className="max-w-[92%] space-y-2">
                {m.text && (
                  <div
                    className="text-[12.5px] leading-relaxed px-3 py-2 rounded-2xl rounded-bl-sm"
                    style={{ background: m.unknown ? '#f3ebd7' : '#f4f0e8', color: m.unknown ? GOLD : '#08111f' }}
                  >
                    {m.text}
                  </div>
                )}
                {(m.topics || []).map((topic) => (
                  <div
                    key={topic.id}
                    // pre-line keeps the answer's own line breaks: the answers
                    // are written as points because a paragraph in a 370px
                    // panel is a wall. leading-6 gives the points room.
                    className="text-[12.5px] leading-6 px-3 py-2.5 rounded-2xl rounded-bl-sm whitespace-pre-line"
                    style={{ background: '#f4f0e8', color: '#08111f' }}
                  >
                    <div className="font-semibold mb-1">{topic.title}</div>
                    {topic.answer}
                    {topic.opens && NAV_ROUTES[topic.opens] && (
                      <button
                        type="button"
                        onClick={() => { navigate(NAV_ROUTES[topic.opens]); setOpen(false); }}
                        className="mt-2 flex items-center gap-1 text-[11.5px] font-semibold"
                        style={{ color: TEAL }}
                      >
                        Take me there <FiExternalLink size={12} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        {busy && <div className="text-[11.5px]" style={{ color: MUTED }}>Looking…</div>}

        {/* The honest exit: no answer here, so hand it to a person with the
            question already written down. */}
        {unanswered && !busy && (
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={raiseTicket}
              disabled={raising}
              className="text-[11.5px] font-semibold px-3 py-1.5 rounded-lg text-white disabled:opacity-60"
              style={{ background: GOLD }}
            >
              {raising ? 'Raising…' : 'Ask a person'}
            </button>
            <button
              type="button"
              onClick={() => { navigate(ROUTES.AGENT); setOpen(false); }}
              className="text-[11.5px] font-semibold px-3 py-1.5 rounded-lg"
              style={{ border: `1px solid ${BORDER}`, color: '#08111f' }}
            >
              Ask Vee instead
            </button>
          </div>
        )}
        <div ref={endRef} />
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); askQuestion(input); }}
        className="flex items-center gap-2 px-3 py-2.5"
        style={{ borderTop: `1px solid ${BORDER}` }}
      >
        <input
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="How do I…"
          maxLength={500}
          className="flex-1 text-[12.5px] px-3 py-2 rounded-lg outline-none"
          style={{ background: '#f4f0e8', border: `1px solid ${BORDER}`, color: '#08111f' }}
        />
        <button
          type="submit"
          disabled={!input.trim() || busy}
          aria-label="Send"
          className="w-9 h-9 rounded-lg flex items-center justify-center text-white disabled:opacity-40"
          style={{ background: TEAL }}
        >
          <FiSend size={15} />
        </button>
      </form>

      <button
        type="button"
        onClick={() => { navigate(ROUTES.SUPPORT); setOpen(false); }}
        className="text-[11px] py-2 text-center hover:underline"
        style={{ color: MUTED, borderTop: `1px solid ${BORDER}` }}
      >
        My support tickets
      </button>
    </div>
  );
}
